import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { HiCalendar, HiLocationMarker, HiClock, HiPhone, HiMail } from 'react-icons/hi'
import { getLocalizedEvents, isEventPast } from '../../data/eventsData'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair } from '../../utils/seoUtils'
import './Events.css'

function Events() {
  const { t, i18n } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Получаем локализованные события
  const localizedEvents = getLocalizedEvents(i18n.language)
  
  // Определяем начальный фильтр: если есть прошедшие события и фильтр не указан, показываем "past"
  const urlFilter = searchParams.get('filter')
  const hasPastEvents = localizedEvents.some(event => isEventPast(event))
  const hasUpcomingEvents = localizedEvents.some(event => !isEventPast(event))
  
  // Если фильтр не указан в URL и есть прошедшие события, автоматически переключаемся на "past"
  const initialFilter = urlFilter || (hasPastEvents && !hasUpcomingEvents ? 'past' : 'all')
  const [filter, setFilter] = useState(initialFilter)
  
  // Обновляем URL при изменении фильтра
  useEffect(() => {
    const params = new URLSearchParams()
    if (filter !== 'all') params.set('filter', filter)
    setSearchParams(params, { replace: true })
  }, [filter, setSearchParams])
  
  // Автоматически переключаемся на "past" если все события прошедшие
  useEffect(() => {
    if (filter === 'all' && hasPastEvents && !hasUpcomingEvents) {
      setFilter('past')
    }
  }, [hasPastEvents, hasUpcomingEvents])
  
  const filteredEvents = localizedEvents.filter(event => {
    if (filter === 'upcoming') {
      return !isEventPast(event)
    } else if (filter === 'past') {
      return isEventPast(event)
    }
    return true
  })

  const breadcrumbItems = useMemo(
    () => seoBreadcrumbPair(t('common.home'), t('common.events'), 'events'),
    [t]
  )

  return (
    <div className="events-page">
      <SEO 
        title={t('common.events')}
        description={t('events.description', 'Upcoming and past events of SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="events-hero">
          <h1>{t('common.events')}</h1>
          <p className="events-intro">
            {t('events.intro', 'Stay updated with competitions, seminars, workshops, and other events organized by SUMA International and its member federations.')}
          </p>
        </div>

        <div className="events-filter">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            {t('events.all', 'All Events')}
          </button>
          <button 
            className={`filter-btn ${filter === 'upcoming' ? 'active' : ''}`}
            onClick={() => setFilter('upcoming')}
          >
            {t('events.upcoming', 'Upcoming')}
          </button>
          <button 
            className={`filter-btn ${filter === 'past' ? 'active' : ''}`}
            onClick={() => setFilter('past')}
          >
            {t('events.past', 'Past Events')}
          </button>
        </div>

        <div className="events-list">
          {filteredEvents.length > 0 ? (
            filteredEvents.map(event => (
              <div key={event.id} className="event-card">
                {event.poster && (
                  <div className="event-poster">
                    <img src={event.poster} alt={event.title} />
                  </div>
                )}
                <div className="event-card-content">
                  <div className="event-date">
                    <HiCalendar className="event-icon" />
                    <span>{event.dateFormatted || event.date}</span>
                  </div>
                  <h3>{event.title}</h3>
                  <div className="event-info">
                    <div className="event-location">
                      <HiLocationMarker className="info-icon" />
                      <span>{event.location}</span>
                    </div>
                  </div>
                  <p className="event-description" style={{ whiteSpace: 'pre-line' }}>{event.description}</p>
                  {(event.phone || event.email || event.website) && (
                    <div className="event-contacts">
                      {event.phone && (
                        <div className="event-contact">
                          <HiPhone className="contact-icon" />
                          <a href={`tel:${event.phone.replace(/\s/g, '')}`}>{event.phone}</a>
                        </div>
                      )}
                      {event.email && (
                        <div className="event-contact">
                          <HiMail className="contact-icon" />
                          <a href={`mailto:${event.email}`}>{event.email}</a>
                        </div>
                      )}
                      {event.website && (
                        <div className="event-contact">
                          <span className="event-website">{event.website}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="no-events">
              <p>
                {filter !== 'all' 
                  ? t('events.noResults', 'No events found matching your filter.') 
                  : t('events.noEvents', 'Events will be displayed here as they are added to the system.')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Events
